import { z } from 'zod'
import { AgentProvenanceEntrySchema } from './agent_provenance_entry.ts'
import { ContactAccountsModelSchema, ContactAccountsModelFields } from './contact_accounts_model.ts'
import { SelectListModelSchema } from './select_list_model.ts'
import { CompanyTeamEntrySchema } from './company_team_entry.ts'
import { makeFieldKeys, validateWithZodSchema } from '../../core/utils/utilities.ts'
import { ContactsModelFields } from './contacts_model.ts'
import {
  TABLE_CONTACTS,
  TABLE_CONTACT_ACCOUNTS,
  FIELD_KEY_SEPARATOR,
  FIELD_KEY_PROG,
  FIELD_KEY_LABEL
} from '../globals.ts'

const ALIAS_CONTACTS = `${TABLE_CONTACTS}${FIELD_KEY_SEPARATOR}` as const
const ALIAS_CONTACT_ACCOUNTS = `${TABLE_CONTACT_ACCOUNTS}${FIELD_KEY_SEPARATOR}` as const

const PROG_CURRENT_ROLES = `${FIELD_KEY_PROG}current_roles` as const
const PROG_PRIOR_ROLES = `${FIELD_KEY_PROG}prior_roles` as const
const PROG_FOUNDER_OF = `${FIELD_KEY_PROG}founder_of` as const
const PROG_INTRODUCTIONS = `${FIELD_KEY_PROG}introductions` as const
const PROG_ACCOUNT_NAME = `${FIELD_KEY_PROG}account_name` as const
const PROG_RELATIONSHIP_CATEGORY_NAME = `${FIELD_KEY_PROG}relationship_category_name` as const

const FIELD_CONTACT_ID = `${ALIAS_CONTACTS}${ContactsModelFields.id}` as const
const FIELD_CONTACT_FIRST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.first_name}` as const
const FIELD_CONTACT_MIDDLE_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.middle_name}` as const
const FIELD_CONTACT_LAST_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.last_name}` as const
const FIELD_CONTACT_PREFERRED_NAME = `${ALIAS_CONTACTS}${ContactsModelFields.preferred_name}` as const
const FIELD_CONTACT_EMAIL = `${ALIAS_CONTACTS}${ContactsModelFields.email}` as const
const FIELD_CONTACT_EMAIL_OTHER = `${ALIAS_CONTACTS}${ContactsModelFields.email_other}` as const
const FIELD_CONTACT_EMAIL_PRIVATE = `${ALIAS_CONTACTS}${ContactsModelFields.email_private}` as const
const FIELD_CONTACT_BUSINESS_PHONE = `${ALIAS_CONTACTS}${ContactsModelFields.business_phone}` as const
const FIELD_CONTACT_MOBILE_PHONE = `${ALIAS_CONTACTS}${ContactsModelFields.mobile_phone}` as const
const FIELD_CONTACT_GENDER = `${ALIAS_CONTACTS}${ContactsModelFields.gender}` as const
const FIELD_CONTACT_GENDER_LABEL = `${ALIAS_CONTACTS}${ContactsModelFields.gender}__label` as const
const FIELD_CONTACT_ADDRESS_1 = `${ALIAS_CONTACTS}${ContactsModelFields.address_1}` as const
const FIELD_CONTACT_ADDRESS_2 = `${ALIAS_CONTACTS}${ContactsModelFields.address_2}` as const
const FIELD_CONTACT_ADDRESS_3 = `${ALIAS_CONTACTS}${ContactsModelFields.address_3}` as const
const FIELD_CONTACT_ADDRESS_CITY_LABEL = `${ALIAS_CONTACTS}${ContactsModelFields.address_city_label}` as const
const FIELD_CONTACT_ADDRESS_CITY_CODE = `${ALIAS_CONTACTS}${ContactsModelFields.address_city_code}` as const
const FIELD_CONTACT_ADDRESS_STATEPROVINCE = `${ALIAS_CONTACTS}${ContactsModelFields.address_stateprovince}` as const
const FIELD_CONTACT_ADDRESS_POSTAL_CODE = `${ALIAS_CONTACTS}${ContactsModelFields.address_postal_code}` as const
const FIELD_CONTACT_ADDRESS_COUNTRY_LABEL = `${ALIAS_CONTACTS}${ContactsModelFields.address_country_label}` as const
const FIELD_CONTACT_ADDRESS_COUNTRY_CODE = `${ALIAS_CONTACTS}${ContactsModelFields.address_country_code}` as const
const FIELD_CONTACT_LINKED_IN_URL = `${ALIAS_CONTACTS}${ContactsModelFields.linked_in_url}` as const
const FIELD_CONTACT_PHOTO_URL = `${ALIAS_CONTACTS}${ContactsModelFields.photo_url}` as const
const FIELD_CONTACT_NOTES = `${ALIAS_CONTACTS}${ContactsModelFields.notes}` as const

const FIELD_CONTACT_ACCOUNTS_ACCOUNT_ID = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.account_id}` as const
const FIELD_CONTACT_ACCOUNTS_RELATIONSHIP_CATEGORY_ID = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.relationship_category_id}` as const
const FIELD_CONTACT_ACCOUNTS_TITLE = `${ALIAS_CONTACT_ACCOUNTS}${ContactAccountsModelFields.title}` as const

// Agent Access 4.5: null unless an agent session created this record.
const PROG_CREATED_PROVENANCE = `${FIELD_KEY_PROG}created_provenance` as const

export const ContactRecordSchema = z
  .object({
    [FIELD_CONTACT_ID]: z.string().nullish(),
    [FIELD_CONTACT_FIRST_NAME]: z.string().nullish(),
    [FIELD_CONTACT_MIDDLE_NAME]: z.string().nullish(),
    [FIELD_CONTACT_LAST_NAME]: z.string().nullish(),
    [FIELD_CONTACT_PREFERRED_NAME]: z.string().nullish(),
    [FIELD_CONTACT_EMAIL]: z.string().nullish(),
    [FIELD_CONTACT_EMAIL_OTHER]: z.string().nullish(),
    [FIELD_CONTACT_EMAIL_PRIVATE]: z.string().nullish(),
    [FIELD_CONTACT_BUSINESS_PHONE]: z.string().nullish(),
    [FIELD_CONTACT_MOBILE_PHONE]: z.string().nullish(),
    [FIELD_CONTACT_GENDER]: z.string().nullish(),
    [FIELD_CONTACT_GENDER_LABEL]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_1]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_2]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_3]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_CITY_LABEL]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_CITY_CODE]: z.number().nullish(),
    [FIELD_CONTACT_ADDRESS_STATEPROVINCE]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_POSTAL_CODE]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_COUNTRY_LABEL]: z.string().nullish(),
    [FIELD_CONTACT_ADDRESS_COUNTRY_CODE]: z.string().nullish(),
    [FIELD_CONTACT_LINKED_IN_URL]: z.string().nullish(),
    [FIELD_CONTACT_PHOTO_URL]: z.string().nullish(),
    [FIELD_CONTACT_NOTES]: z.string().nullish(),

    [FIELD_CONTACT_ACCOUNTS_ACCOUNT_ID]: z.string().nullish(),
    [FIELD_CONTACT_ACCOUNTS_RELATIONSHIP_CATEGORY_ID]: z.string().nullish(),
    [FIELD_CONTACT_ACCOUNTS_TITLE]: z.string().nullish(),

    [PROG_CURRENT_ROLES]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_PRIOR_ROLES]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_FOUNDER_OF]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_INTRODUCTIONS]: z.array(CompanyTeamEntrySchema).nullish(),
    [PROG_ACCOUNT_NAME]: z.string().nullish(), // TODO: needs to change
    [PROG_RELATIONSHIP_CATEGORY_NAME]: z.string().nullish(),
    [PROG_CREATED_PROVENANCE]: AgentProvenanceEntrySchema.nullish()
  })
  .passthrough()

export const ContactRecordFields = makeFieldKeys(ContactRecordSchema)

export type ContactRecord = z.infer<typeof ContactRecordSchema>

export function validateContactRecord(data: unknown, options: { isArray: true }): ContactRecord[]
export function validateContactRecord(data: unknown, options?: { isArray?: false }): ContactRecord
export function validateContactRecord(data: unknown, options?: { isArray?: boolean }): ContactRecord | ContactRecord[]
export function validateContactRecord(data: unknown, options?: { isArray?: boolean }): ContactRecord | ContactRecord[] {
  try {
    return validateWithZodSchema(ContactRecordSchema, data, 'contact record', options)
  } catch (error) {
    throw new Error(`Contact record validation failed: ${error}`)
  }
}
